import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../auth/auth.service';
import {
  UiSwitcherRouteKey,
  UiSwitcherRouteMatch,
  matchLegacyPath,
  toPhoenixPath,
} from './ui-switcher-route.map';

interface HandoverTokenResponse {
  apiStatus: string;
  handoverToken: string;
  expiresIn: number;
}

// phoenixUrl isn't set in every environment file yet (e.g. QA1-8, UAT) —
// typed as optional here rather than assumed present on the shared literal.
interface PureSpectrumEnvironment {
  phoenixUrl?: string;
}

/** URL fragment key the handover token travels under, never a query param. */
const HANDOVER_FRAGMENT_KEY = 'ht';

@Injectable({ providedIn: 'root' })
export class UiSwitcherService {
  private _baseUrl = environment.pureSpectrum.url;

  constructor(private _http: HttpClient, private _auth: AuthService) {}

  /** Buyer role (server-enforced too, see HasRole(['buyer']) on the handover-token endpoint) AND the enableUiSwitcher flag. */
  canSwitch(): boolean {
    return (
      this._auth.userType === 'buyer' &&
      !!this._auth.buyerConfig?.enableUiSwitcher
    );
  }

  matchCurrentRoute(url: string): UiSwitcherRouteMatch | null {
    return matchLegacyPath(url.split('?')[0].split('#')[0]);
  }

  createHandoverToken(): Observable<HandoverTokenResponse> {
    return this._http.post<HandoverTokenResponse>(
      `${this._baseUrl}/auth/handover-token`,
      null
    );
  }

  /** Requests a handover token and redirects to the equivalent Phoenix route. */
  switchToPhoenix(
    routeKey: UiSwitcherRouteKey,
    params: Record<string, string | number> = {}
  ): Observable<void> {
    const phoenixUrl = (environment.pureSpectrum as PureSpectrumEnvironment)
      .phoenixUrl;
    if (!phoenixUrl) {
      return throwError(
        new Error('phoenixUrl is not configured for this environment')
      );
    }

    return this.createHandoverToken().pipe(
      tap(({ handoverToken }) => {
        const path = toPhoenixPath(routeKey, params);
        window.location.href = `${phoenixUrl}${path}#${HANDOVER_FRAGMENT_KEY}=${handoverToken}`;
      }),
      map(() => undefined)
    );
  }
}
